import { useFinancialAccount } from "../../contexts/FinancialAccountContext";
import { FinancialAccount } from "../../models/FinancialAccount";
import { useState } from "react";
import { Table, TableBody, TableCell, TableRow } from "../../design/table";
import { Button } from "../../design/button";
import { PlusIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { Field } from "@headlessui/react";
import { Label } from "../../design/fieldset";
import { useChooseFinancialAccountDialog } from "../../providers/ChooseFinancialAccountDialogProvider";
import { Subheading } from "../../design/heading";

interface SelectFinancialAccountDialogProps {
  title?: string;
  values: FinancialAccount["_id"][];
  onChange: (value: FinancialAccount["_id"][]) => void;
}

const SelectFinancialAccountsField: React.FC<
  SelectFinancialAccountDialogProps
> = ({ title, values, onChange }) => {
  const { financialAccounts } = useFinancialAccount();
  const { openDialog } = useChooseFinancialAccountDialog();

  const onFinancialAccountSelected = (financialAccount: FinancialAccount) => {
    if (!values.includes(financialAccount._id)) {
      onChange([...values, financialAccount._id]);
    } else {
      onChange(values.filter((id) => id !== financialAccount._id));
    }
  };

  const removeFinancialAccount = (id: string) => {
    onChange(values.filter((pid) => pid !== id));
  };

  const selectedFinancialAccounts = financialAccounts.filter((p) =>
    values.includes(p._id),
  );

  return (
    <Field>
      <div className="flex items-center justify-between mb-2">
        <Label>{title}</Label>
        <Button
          onClick={() => openDialog(undefined, onFinancialAccountSelected)}
        >
          <PlusIcon className="w-5 h-5 mr-2" />
        </Button>
      </div>

      <div className="overflow-hidden border rounded-lg">
        <Table>
          <TableBody>
            {selectedFinancialAccounts.length > 0 ? (
              selectedFinancialAccounts.map((financialAccount) => (
                <TableRow key={financialAccount._id}>
                  <TableCell>
                    <Subheading>{`${financialAccount.name} (${financialAccount.mask})`}</Subheading>
                  </TableCell>
                  <TableCell>
                    {financialAccount.currentBalance.toLocaleString("en-US", {
                      style: "currency",
                      currency: financialAccount.currency,
                    })}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      color="red"
                      onClick={() =>
                        removeFinancialAccount(financialAccount._id)
                      }
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} className="text-center text-gray-500">
                  No financialAccounts selected
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Field>
  );
};

export default SelectFinancialAccountsField;
